import {
  compareAsc,
  formatDistanceToNow,
  isValid,
  parseISO,
  startOfDay,
} from 'date-fns';
import { type TFunction } from 'i18next';

import { StatusBadge } from '@/components/common';
import { Button } from '@/components/ui/button';
import { dateOnlyFormatter } from '@/grid/value-formatters/dateOnlyFormatter';
import { formatShortSessionId } from '@/utils/formatting';

import type { ImplantSession, ImplantSessionStatus } from '../types';

const statusColors: Record<ImplantSessionStatus, { bg: string; text: string }> =
  {
    Active: { bg: 'bg-green-100', text: 'text-green-700' },
    Completed: { bg: 'bg-blue-100', text: 'text-blue-700' },
    Draft: { bg: 'bg-gray-100', text: 'text-gray-700' },
  };

export const formatRelativeTime = (value: string | null | undefined) => {
  if (!value) return '—';
  const date = parseISO(value);
  if (!isValid(date)) return '—';
  return formatDistanceToNow(date, { addSuffix: true });
};

const dayFilteredDateColumn = (toCellDay: (cellValue: string) => Date) => ({
  filter: 'agDateColumnFilter',
  filterParams: {
    comparator: (
      filterLocalDateAtMidnight: Date,
      cellValue: string | null | undefined,
    ) => {
      const cellDay = cellValue ? toCellDay(cellValue) : null;
      return cellDay && isValid(cellDay)
        ? compareAsc(cellDay, filterLocalDateAtMidnight)
        : -1;
    },
    buttons: ['reset'],
    maxNumConditions: 1,
  },
});

export const getSessionColumnDefs = (
  t: TFunction,
  onOpenSession: (session: ImplantSession) => void,
) => [
  {
    field: 'sessionDate',
    headerName: t('onFarmImplant.grid.sessionDate'),
    flex: 1,
    minWidth: 120,
    valueFormatter: dateOnlyFormatter,
    ...dayFilteredDateColumn((cellValue) => parseISO(cellValue.slice(0, 10))),
  },
  {
    field: 'sessionId',
    headerName: t('onFarmImplant.grid.sessionId'),
    flex: 1,
    minWidth: 140,
    cellClass: 'text-blue-600 font-mono text-xs',
    cellRenderer: (params: { value: string }) => (
      <span title={params.value}>{formatShortSessionId(params.value)}</span>
    ),
  },
  {
    field: 'tripNumber',
    headerName: t('onFarmImplant.grid.tripNumber'),
    flex: 1,
    minWidth: 120,
    valueFormatter: (params: { value: string | null }) => params.value ?? '—',
  },
  {
    field: 'accountName',
    headerName: t('onFarmImplant.grid.account'),
    flex: 1.2,
    minWidth: 160,
    valueFormatter: (params: { value: string | null }) => params.value ?? '—',
  },
  {
    field: 'createdByName',
    headerName: t('onFarmImplant.grid.createdBy'),
    flex: 1,
    minWidth: 120,
    valueFormatter: (params: { value: string | null }) => params.value ?? '—',
  },
  {
    field: 'status',
    headerName: t('onFarmImplant.grid.status'),
    flex: 0.8,
    minWidth: 110,
    cellRenderer: (params: { value: string }) => (
      <StatusBadge label={params.value} colorMap={statusColors} />
    ),
  },
  {
    field: 'updatedAt',
    headerName: t('onFarmImplant.grid.lastUpdated'),
    flex: 1,
    minWidth: 120,
    valueFormatter: (params: { value: string | null }) =>
      formatRelativeTime(params.value),
    ...dayFilteredDateColumn((cellValue) => startOfDay(parseISO(cellValue))),
  },
  {
    headerName: t('onFarmImplant.grid.actions'),
    flex: 1,
    minWidth: 140,
    sortable: false,
    filter: false,
    cellRenderer: (params: { data: ImplantSession }) => {
      if (!params.data) return null;
      return (
        <div className="flex items-center gap-2 py-1">
          <Button size="sm" onClick={() => onOpenSession(params.data)}>
            {t('onFarmImplant.grid.openSession')}
            <i className="pi pi-arrow-right ml-1 text-xs" />
          </Button>
        </div>
      );
    },
  },
];
